/**
 * Redirects Tool
 *
 * Tool implementation for QR Redirect tracking
 */

import { FaExchangeAlt } from "react-icons/fa";
import { BaseTool } from "../BaseTool";
import { redirectsApi } from "../../services/toolsApi";

export class RedirectsTool extends BaseTool {
  constructor() {
    super();

    // Metadata
    this.id = "redirects";
    this.name = "Redirects";
    this.description = "Redirect tracking and campaign management";
    this.icon = FaExchangeAlt;
    this.color = "#4299E1";
    this.path = "/dashboard/tools/redirects";

    // Collections
    this.collections = ["qr_scans", "qr_campaigns", "qr_daily_stats"];

    // Access control
    this.requiresAdmin = false;

    // Summary configuration
    this.summaryConfig = {
      enabled: true,
      mainStat: "todayScans",
      expectedStatCount: 3, // todayScans, totalCampaigns, totalScans
    };

    // Filter configuration
    this.filterConfig = {
      search: {
        enabled: false, // Redirects uses campaign cards, not search
      },
      buttonGroups: [
        {
          label: "Region",
          key: "country",
          defaultValue: "global",
          options: [
            { value: "global", label: "Global", icon: "FaGlobe" },
            { value: "uk", label: "UK", icon: "FaFlag" },
          ],
        },
        {
          label: "Traffic Type",
          key: "traffic",
          defaultValue: "all",
          options: [
            { value: "all", label: "All Traffic", icon: "FaUsers" },
            { value: "unique", label: "Unique Only", icon: "FaUser" },
          ],
        },
      ],
      sortOptions: [
        { value: "today", label: "Today's redirects" },
        { value: "total", label: "Total redirects" },
        { value: "recent", label: "Recently Updated" },
        { value: "alpha", label: "Alphabetical" },
      ],
      defaultSort: "today",
    };
  }

  /**
   * Get summary data for dashboard widget
   */
  async getSummaryData() {
    try {
      const response = await redirectsApi.getSummary();

      if (!response || !response.summary) {
        throw new Error("Invalid response from redirectsApi");
      }

      return {
        success: true,
        data: {
          todayScans: response.summary.todayScans || 0,
          totalCampaigns: response.summary.totalCampaigns || 0,
          totalScans: response.summary.totalScans || 0,
        },
        lastUpdated: response.lastUpdated
          ? new Date(response.lastUpdated)
          : new Date(),
      };
    } catch (error) {
      console.error("Error fetching redirects summary:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get detailed stats with filters
   */
  async getDetailedStats(filters = {}) {
    try {
      const { country = "global", trafficType = "all" } = filters;
      const data = await redirectsApi.getAllStats({ country, trafficType });

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Custom backup preparation
   * Could exclude test campaigns or old data
   */
  async prepareBackup(collections, format) {
    // Could add logic to exclude test campaigns
    // For example: campaigns with ID starting with 'test-'
    return collections;
  }
}

export default RedirectsTool;
